import { SearchCategories } from 'src/app/components/search/search.enum';
import { CustomMarkerBuilderI, MapState, ProcessMapSearchResponseStrategy } from 'src/app/interfaces/map.interface';
import { CustomMarker } from 'src/app/models/custom-marker.model';
import { SearchAggregationBucket } from 'src/app/swagger-api/model/searchAggregationBucket';
import { SearchAggregationResponse } from 'src/app/swagger-api/model/searchAggregationResponse';
import { SearchResultAggregation } from 'src/app/swagger-api/model/searchResultAggregation';
import { SearchNestedAggregation } from '../../../../swagger-api';
import { AggregatedItemTypes } from '../../map.enum';
import { AggregationKey } from '../../map.interfaces';
import { CustomMarkerBuilderFactory } from '../builders/marker-creator.method';
import { getSearchAggregationDecoratedResponse } from '../decorators/decorators.helpers';

export class AggregatedItemsProcessor implements ProcessMapSearchResponseStrategy {
  private readonly aggregatedMapper: { [key: string]: AggregatedItemTypes } = {
    [SearchCategories.PROJECTS]: AggregatedItemTypes.AGGREGATION_PROJECT,
    [SearchCategories.COMMUNITIES]: AggregatedItemTypes.AGGREGATION_COMMUNITY,
    [SearchCategories.ORGANIZATIONS]: AggregatedItemTypes.AGGREGATION_ORGANIZATION,
    [SearchCategories.DOCTORS]: AggregatedItemTypes.AGGREGATION_DOCTOR,
    [SearchCategories.PATIENTS]: AggregatedItemTypes.AGGREGATION_PATIENT,
    [SearchCategories.RESEARCHERS]: AggregatedItemTypes.AGGREGATION_RESEARCHER,
    [SearchCategories.MED_STAFF]: AggregatedItemTypes.AGGREGATION_MED_STAFF,
  };

  public constructor(
    private response: SearchAggregationResponse,
    private state: MapState,
  ) {}

  public getMarkers(): CustomMarker[] {
    const markers: CustomMarker[] = [];

    getSearchAggregationDecoratedResponse(this.response, this.state).forEach((result: SearchResultAggregation) => {
      result.aggregated_entities.forEach((entities: string) => {
        const builder: CustomMarkerBuilderI = CustomMarkerBuilderFactory.getBuilder(this.aggregatedMapper[entities]);

        result.buckets.forEach((bucket: SearchAggregationBucket) => {
          const nestedMarkers: CustomMarker[] = this.createBucketAggregationMarkers(builder, bucket);
          markers.push(...nestedMarkers);
        });
      });
    });
    return markers;
  }

  private createBucketAggregationMarkers(
    builder: CustomMarkerBuilderI,
    bucket: SearchAggregationBucket,
  ): CustomMarker[] {
    const resultingMarkers: CustomMarker[] = [];

    if (bucket.nested_aggregations && bucket.nested_aggregations.length > 0) {
      bucket.nested_aggregations.forEach((nestedAggregation: SearchNestedAggregation) => {
        if (nestedAggregation.nested_buckets.length > 0) {
          nestedAggregation.nested_buckets.forEach((nestedBucket: SearchAggregationBucket) => {
            const nestedMarkers: CustomMarker[] = this.createBucketAggregationMarkers(builder, nestedBucket);
            resultingMarkers.push(...nestedMarkers);
          });
        } else {
          const newMarker: CustomMarker | null = this.markerFromBucket(builder, bucket);
          if (newMarker) {
            resultingMarkers.push(newMarker);
          }
        }
      });
    } else {
      const newMarker: CustomMarker | null = this.markerFromBucket(builder, bucket);
      if (newMarker) {
        resultingMarkers.push(newMarker);
      }
    }

    return resultingMarkers;
  }

  private markerFromBucket(builder: CustomMarkerBuilderI, bucket: SearchAggregationBucket): CustomMarker | null {
    const aggregationKey: AggregationKey = bucket.aggregation_key as AggregationKey;
    if (aggregationKey.center_coordinates) {
      builder.setCoords(aggregationKey.center_coordinates.lat, aggregationKey.center_coordinates.lon);
      builder.toggleIsAggregatedFlag();
      builder.setIcon(bucket.aggregated_items_count);
      builder.setCounterLabel(bucket.aggregated_items_count);
      builder.setMarkerId(aggregationKey.center_coordinates);
      builder.setLocationLiteral(aggregationKey.location_literal);
      builder.setTooltips(aggregationKey.tooltips);
      builder.setItemsKind();
      builder.setItemsCount(bucket.aggregated_items_count);
      return builder.buildMarker();
    }

    return null;
  }
}
